"use client";

import { CheckCircle2, Trophy, Users, ShieldCheck, Microscope, Wrench } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { TimelineSection } from "@/components/features/about/timeline-section";
import { PartnershipSection } from "@/components/features/about/partnership-section";
import { motion } from "framer-motion";
import { PageBackground } from "@/components/ui/page-background";
import { pageTheme } from "@/lib/theme/page-theme";

export function AboutClient() {
  const { t } = useLanguage();

  const pillarIcons = [
    ShieldCheck,
    Trophy,
    Users,
    ShieldCheck,
    Microscope,
    Wrench
  ];

  return (
    <div className={pageTheme.wrapper}>
      <PageBackground />

      {/* Ambient decorative orbs */}
      <div aria-hidden="true" className={pageTheme.orbRight} />
      <div aria-hidden="true" className={pageTheme.orbLeft} />

      <div className="relative z-10">
        {/* Editorial Header */}
        <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center mb-16 md:mb-24">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={pageTheme.pageTag}
          >
            {t.onas.headerTag}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-7xl text-balance"
          >
            {t.onas.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 text-xl leading-relaxed text-zinc-600 dark:text-zinc-400"
          >
            {t.onas.desc}
          </motion.p>
        </section>

        {/* Narrative Timeline */}
        <section className="mb-16 md:mb-20">
          <TimelineSection />
        </section>

        {/* Partnerships */}
        <section className="mb-32">
          <PartnershipSection />
        </section>

        {/* Foundations Grid (Bento Style) */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-32">
          <div className="text-center mb-16">
            <h2 className={pageTheme.sectionTitle}>{t.onas.pillarsTitle}</h2>
            <div className="h-1 w-20 bg-orange-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {t.onas.pillars.map((item, idx) => {
              const Icon = pillarIcons[idx] || CheckCircle2;
              const total = t.onas.pillars.length;
              const remainderLg = total % 3;
              const isLastRowPairOnLg = remainderLg === 2 && idx >= total - 2;
              return (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className={`${pageTheme.pillarCard} lg:col-span-2 ${isLastRowPairOnLg ? "lg:col-span-3" : ""}`}
                >
                  <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Icon className="h-24 w-24 text-zinc-900 dark:text-white" />
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className={pageTheme.pillarIcon}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-lg font-bold text-zinc-900 dark:text-zinc-100 leading-snug">
                      {item}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* TODO: Dodać zdjęcia kiedy będą */}

        {/* De-emphasized Gallery */}
        {/* <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-900 dark:bg-zinc-950 rounded-[2.5rem] p-8 md:p-12 overflow-hidden shadow-2xl ring-1 ring-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
              <div className="max-w-xl">
                <h2 className="text-3xl font-bold text-white mb-4">{t.onas.galleryTitle}</h2>
                <p className="text-zinc-400">
                  {t.onas.galleryDesc}
                </p>
              </div>
              <div className="shrink-0 flex gap-2">
                <div className="h-2 w-2 rounded-full bg-orange-500" />
                <div className="h-2 w-2 rounded-full bg-zinc-700" />
                <div className="h-2 w-2 rounded-full bg-zinc-700" />
              </div>
            </div>

            <div className="relative aspect-video lg:aspect-[21/9] w-full max-w-[1200px] mx-auto rounded-3xl overflow-hidden">
              <ImageCarousel />
            </div>
          </div>
        </section> */}
      </div>
    </div>
  );
}
